import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ChevronDown, ChevronRight, DownloadCloud, Hash, Server, Upload } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { AdminTable, type AdminTableColumn, type SortState } from "@/components/ui/admin-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MobileFilterField, MobileFilters } from "@/components/ui/mobile-filters";
import { PageHeader } from "@/components/ui/page-header";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/features/auth/use-auth";
import {
	type CennikZrodlo,
	type CennikZrodloOverviewRow,
	type ContractorRow,
	contractsApi,
	type ImportZrodlaReport,
} from "@/features/contracts/contracts-api";
import { ProductSearchBar } from "@/features/products/components/product-search-bar";
import { useIsBelowMd } from "@/hooks/use-mobile";
import { loadExcelJs } from "@/lib/dynamic-import";
import { ensureElementVisible } from "@/lib/route-guards";
import { useDebounce } from "@/lib/use-debounce";

// Warianty muszą pokrywać się z gałęziami SQL `cena_po_rabatach` oraz FORMULA_WARIANTY (backend).
const FORMULA_VARIANTS = [
	{ value: "standard", label: "Standard", wzor: "cena_100 × (100 − WH − rabat − dokładka)/100" },
	{
		value: "dokladka_od_bazy",
		label: "Dokładka od bazy",
		wzor: "(cena_100 × (100 − WH − rabat)/100) × (100 − dokładka)/100",
	},
] as const;

const WZOR_BY_VALUE = new Map<string, string>(FORMULA_VARIANTS.map((v) => [v.value, v.wzor]));

/** Komórka edycji wariantu formuły ceny. Select dla super_admina, inaczej read-only. */
function FormulaCell({ row }: { row: ContractorRow }) {
	const { hasRole } = useAuth();
	const isSuperAdmin = hasRole("super_admin");
	const queryClient = useQueryClient();
	const current = row.formulaWariant ?? "standard";
	const wzor = WZOR_BY_VALUE.get(current) ?? WZOR_BY_VALUE.get("standard");

	const mutation = useMutation({
		mutationFn: (wariant: string) => contractsApi.setContractorFormula(row.id, wariant),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["contractors", "list-full"] }),
	});

	if (!isSuperAdmin) {
		const label = FORMULA_VARIANTS.find((v) => v.value === current)?.label ?? current;
		return (
			<div className="flex flex-col gap-0.5">
				<span className="text-sm">{label}</span>
				<span className="text-[11px] font-mono text-muted-foreground">{wzor}</span>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-1">
			<Select
				value={current}
				onValueChange={(v) => mutation.mutate(v)}
				disabled={mutation.isPending}
			>
				<SelectTrigger size="sm" className="w-[190px]">
					<SelectValue />
				</SelectTrigger>
				<SelectContent>
					{FORMULA_VARIANTS.map((v) => (
						<SelectItem key={v.value} value={v.value}>
							{v.label}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
			<span className="text-[11px] font-mono text-muted-foreground" title={wzor ?? ""}>
				{wzor}
			</span>
			{mutation.isError && (
				<span className="text-[11px] text-status-error-text">Nie zapisano.</span>
			)}
		</div>
	);
}

// ── Źródła cennika hurtowego (FTP) — konfiguracja per segment ────────────────

const ZRODLO_TYPY = [
	{ value: "ftp_xml", label: "FTP XML" },
	{ value: "ftp_frapo", label: "FTP FRAPO" },
	{ value: "comarch", label: "Comarch (nieobsługiwane)" },
] as const;

const SEGMENTY = [
	{ value: "baza", label: "Baza (AW)" },
	{ value: "franczyza", label: "Franczyza (AF)" },
] as const;

type SegmentValue = (typeof SEGMENTY)[number]["value"];

/** Domyślny (pusty) stan formularza dla segmentu bez skonfigurowanego źródła. */
function emptyZrodlo(segment: SegmentValue): CennikZrodlo {
	return {
		segment,
		typ: "ftp_xml",
		aktywne: false,
		host: "",
		port: undefined,
		katalog: "",
		login: "",
		hasloUstawione: false,
		uzyjFtps: false,
	};
}

/** Wartości formularza jednego segmentu. Hasło write-only: puste = zachowaj istniejące. */
export type WartosciZrodla = {
	typ: string;
	aktywne: boolean;
	host: string;
	port: string;
	katalog: string;
	login: string;
	haslo: string;
	uzyjFtps: boolean;
};

/** Wartości startowe z wiersza zwróconego przez API — jednocześnie punkt odniesienia
 *  do wykrycia, czy zakładka była w ogóle ruszana. */
export function wartosciZrodla(source: CennikZrodlo): WartosciZrodla {
	return {
		typ: source.typ,
		aktywne: source.aktywne,
		host: source.host ?? "",
		port: source.port != null ? String(source.port) : "",
		katalog: source.katalog ?? "",
		login: source.login ?? "",
		haslo: "",
		uzyjFtps: source.uzyjFtps,
	};
}

/** Formularz jednego segmentu (baza/franczyza) — KONTROLOWANY przez dialog.
 *
 * Stan mieszka w rodzicu, bo zapis jest wspólny dla obu zakładek: własny przycisk
 * „Zapisz" w każdej zakładce wysyłał tylko swój segment, więc po skonfigurowaniu
 * AW i AF jedno kliknięcie zapisywało połowę, a druga ginęła przy zamknięciu
 * dialogu (zgłoszenie 2026-09-02). */
export function ZrodloSegmentForm({
	source,
	wartosci,
	onChange,
}: {
	source: CennikZrodlo;
	wartosci: WartosciZrodla;
	onChange: (w: WartosciZrodla) => void;
}) {
	// Mapowanie „typ + segment -> plik na FTP" pochodzi z backendu (SSoT razem
	// z fetcherem). staleTime Infinity, bo to stałe z kodu, nie dane.
	const { data: oczekiwanePliki } = useQuery({
		queryKey: ["contractors", "zrodla", "pliki"],
		queryFn: () => contractsApi.getOczekiwanePlikiZrodel(),
		staleTime: Number.POSITIVE_INFINITY,
	});

	const { typ, aktywne, host, port, katalog, login, haslo, uzyjFtps } = wartosci;
	const ustaw = <K extends keyof WartosciZrodla>(klucz: K, v: WartosciZrodla[K]) =>
		onChange({ ...wartosci, [klucz]: v });
	const setTyp = (v: string) => ustaw("typ", v);
	const setAktywne = (v: boolean) => ustaw("aktywne", v);
	const setHost = (v: string) => ustaw("host", v);
	const setPort = (v: string) => ustaw("port", v);
	const setKatalog = (v: string) => ustaw("katalog", v);
	const setLogin = (v: string) => ustaw("login", v);
	const setHaslo = (v: string) => ustaw("haslo", v);
	const setUzyjFtps = (v: boolean) => ustaw("uzyjFtps", v);

	const idPrefix = `zrodlo-${source.segment}`;

	// Typ zapisany w bazie może nie być na liście obsługiwanych — np. `ftp_xls`
	// na bazie, która nie dostała migracji v458. Bez syntetycznej pozycji Radix
	// nie ma czego pokazać i renderuje PUSTE pole, nieodróżnialne od ładowania
	// i od braku konfiguracji. Dokładamy więc wpis dla bieżącej wartości —
	// superadmin widzi, co naprawdę siedzi w bazie, zamiast zgadywać.
	const znanyTyp = ZRODLO_TYPY.some((t) => t.value === typ);
	const opcjeTypu: { value: string; label: string }[] = znanyTyp
		? [...ZRODLO_TYPY]
		: // Pusta wartość jest tu niedozwolona — Radix rzuca na `SelectItem value=""`.
			[...ZRODLO_TYPY, ...(typ ? [{ value: typ, label: `Nieznany typ: ${typ}` }] : [])];

	return (
		<div className="space-y-4">
			<div className="flex flex-col gap-1.5">
				<Label htmlFor={`${idPrefix}-typ`}>Typ źródła</Label>
				<Select value={typ} onValueChange={(v) => setTyp(v as CennikZrodlo["typ"])}>
					<SelectTrigger id={`${idPrefix}-typ`} className="w-full">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						{opcjeTypu.map((t) => (
							<SelectItem key={t.value} value={t.value}>
								{t.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
				{!znanyTyp && (
					<p className="text-[11px] text-status-warning-text">
						Zapisany typ „{typ || "(brak)"}" nie jest już obsługiwany — wybierz inny, inaczej zapis
						zostanie odrzucony.
					</p>
				)}
				{/* Który plik zostanie pobrany dla TEJ zakładki. Bez tego wyboru nie
				    widać: nazwę pliku wyznacza segment, więc konfiguracja wpisana
				    w złą zakładkę pobrałaby cennik drugiego segmentu — błąd
				    niewidoczny w danych, bo ceny wyglądają normalnie. */}
				{oczekiwanePliki?.[typ as CennikZrodlo["typ"]]?.[source.segment] ? (
					<p className="text-[11px] text-muted-foreground">
						Pobierany plik:{" "}
						<span className="font-mono text-foreground">
							{oczekiwanePliki[typ as CennikZrodlo["typ"]]?.[source.segment]}
						</span>
					</p>
				) : null}
			</div>

			<div className="grid grid-cols-2 gap-3">
				<div className="flex flex-col gap-1.5 col-span-2 sm:col-span-1">
					<Label htmlFor={`${idPrefix}-host`}>Host</Label>
					<Input
						id={`${idPrefix}-host`}
						value={host}
						onChange={(e) => setHost(e.target.value)}
						placeholder="ftp.example.pl"
					/>
				</div>
				<div className="flex flex-col gap-1.5 col-span-2 sm:col-span-1">
					<Label htmlFor={`${idPrefix}-port`}>Port</Label>
					<Input
						id={`${idPrefix}-port`}
						type="number"
						value={port}
						onChange={(e) => setPort(e.target.value)}
						placeholder="21"
					/>
				</div>
			</div>

			<div className="flex flex-col gap-1.5">
				<Label htmlFor={`${idPrefix}-katalog`}>Katalog</Label>
				<Input
					id={`${idPrefix}-katalog`}
					value={katalog}
					onChange={(e) => setKatalog(e.target.value)}
					placeholder="/cenniki"
				/>
			</div>

			<div className="flex flex-col gap-1.5">
				<Label htmlFor={`${idPrefix}-login`}>Login</Label>
				<Input
					id={`${idPrefix}-login`}
					value={login}
					onChange={(e) => setLogin(e.target.value)}
					autoComplete="off"
				/>
			</div>

			<div className="flex flex-col gap-1.5">
				<Label htmlFor={`${idPrefix}-haslo`}>Hasło</Label>
				<Input
					id={`${idPrefix}-haslo`}
					type="password"
					value={haslo}
					onChange={(e) => setHaslo(e.target.value)}
					autoComplete="new-password"
					placeholder={
						source.hasloUstawione ? "•••• (ustawione — wpisz aby zmienić)" : "Wpisz hasło"
					}
				/>
			</div>

			<div className="flex flex-wrap gap-x-6 gap-y-2">
				<label
					htmlFor={`${idPrefix}-aktywne`}
					className="flex items-center gap-2 text-sm font-medium cursor-pointer"
				>
					<input
						id={`${idPrefix}-aktywne`}
						type="checkbox"
						checked={aktywne}
						onChange={(e) => setAktywne(e.target.checked)}
						className="size-4 rounded border-border accent-primary"
					/>
					Aktywne
				</label>
				<label
					htmlFor={`${idPrefix}-ftps`}
					className="flex items-center gap-2 text-sm font-medium cursor-pointer"
				>
					<input
						id={`${idPrefix}-ftps`}
						type="checkbox"
						checked={uzyjFtps}
						onChange={(e) => setUzyjFtps(e.target.checked)}
						className="size-4 rounded border-border accent-primary"
					/>
					Użyj FTPS
				</label>
			</div>
		</div>
	);
}

/** Zawartość dialogu — leniwie pobiera źródła po otwarciu, zakładki per segment.
 * `defaultSegment` steruje początkowo aktywną zakładką (baza/franczyza). */
export function ZrodlaDialogBody({
	contractorId,
	defaultSegment,
}: {
	contractorId: number;
	defaultSegment?: SegmentValue;
}) {
	const [aktywny, setAktywny] = useState<SegmentValue>(defaultSegment ?? "baza");
	const queryClient = useQueryClient();

	const { data, isLoading, isError } = useQuery({
		queryKey: ["contractors", "zrodla", contractorId],
		queryFn: () => contractsApi.getContractorSources(contractorId),
	});

	// Referencja `source` MUSI być stabilna między renderami: formularz resynchronizuje
	// się z niej przez `useEffect([source])`, więc świeży obiekt z `emptyZrodlo()` przy
	// każdym renderze kasował wpisane dane za każdym razem, gdy cokolwiek w dialogu
	// zmieniło stan (choćby przełączenie zakładki). Dotyczyło to WYŁĄCZNIE segmentów
	// bez wiersza w bazie — te z wierszem dostawały stabilny obiekt z cache React Query
	// i dlatego problem długo wyglądał na losowy.
	const zrodla = useMemo(() => {
		const wg = new Map<SegmentValue, CennikZrodlo>();
		for (const s of SEGMENTY) {
			wg.set(s.value, data?.find((z) => z.segment === s.value) ?? emptyZrodlo(s.value));
		}
		return wg;
	}, [data]);

	// Wartości formularzy OBU zakładek. Resynchronizacja po `zrodla` (czyli po
	// odświeżeniu z serwera), nie po każdym renderze — inaczej wpisywanie kasowałoby
	// samo siebie.
	const [stan, setStan] = useState<Record<SegmentValue, WartosciZrodla> | null>(null);
	useEffect(() => {
		setStan({
			baza: wartosciZrodla(zrodla.get("baza") as CennikZrodlo),
			franczyza: wartosciZrodla(zrodla.get("franczyza") as CennikZrodlo),
		});
	}, [zrodla]);

	// Zapisujemy TYLKO zakładki realnie zmienione. Wysłanie nietkniętej założyłoby
	// wiersz źródła, o który nikt nie prosił — na liście źródeł wyglądałby jak
	// prawdziwa, tylko wyłączona konfiguracja.
	const zmienione = SEGMENTY.filter((s) => {
		if (!stan) return false;
		const bazowe = wartosciZrodla(zrodla.get(s.value) as CennikZrodlo);
		return (Object.keys(bazowe) as (keyof WartosciZrodla)[]).some(
			(k) => stan[s.value][k] !== bazowe[k],
		);
	});

	const zapis = useMutation({
		mutationFn: async () => {
			if (!stan) return;
			// Sekwencyjnie, nie równolegle: dwa zapisy tego samego kontrahenta idą
			// w ten sam wiersz-per-segment, a przy błędzie chcemy wiedzieć, który
			// segment padł — `Promise.all` gubi tę informację w wyścigu.
			for (const s of zmienione) {
				const w = stan[s.value];
				const port = w.port.trim() === "" ? undefined : Number(w.port);
				await contractsApi.setContractorSource(contractorId, {
					segment: s.value,
					typ: w.typ,
					aktywne: w.aktywne,
					host: w.host.trim() || undefined,
					port: Number.isNaN(port as number) ? undefined : port,
					katalog: w.katalog.trim() || undefined,
					login: w.login.trim() || undefined,
					uzyjFtps: w.uzyjFtps,
					haslo: w.haslo.trim() || undefined,
				});
			}
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["contractors", "zrodla", contractorId] });
			queryClient.invalidateQueries({ queryKey: ["contractors", "list-full"] });
			queryClient.invalidateQueries({ queryKey: ["contractors", "zrodla", "all"] });
			const opis = zmienione.map((s) => s.label).join(" i ");
			toast.success(`Zapisano źródło cennika: ${opis}.`);
		},
		onError: (err: Error) => {
			toast.error(`Nie udało się zapisać: ${err.message}`);
		},
	});

	if (isLoading) {
		return <p className="text-sm text-muted-foreground">Wczytywanie źródeł…</p>;
	}
	if (isError) {
		return <p className="text-sm text-status-error-text">Nie udało się wczytać źródeł.</p>;
	}

	return (
		<Tabs value={aktywny} onValueChange={(v) => setAktywny(v as SegmentValue)}>
			<TabsList className="w-full">
				{SEGMENTY.map((s) => (
					<TabsTrigger key={s.value} value={s.value} className="flex-1">
						{s.label}
					</TabsTrigger>
				))}
			</TabsList>
			{/* `forceMount` trzyma OBA formularze zamontowane — bez tego Radix
			    odmontowuje nieaktywną zakładkę i kasuje wszystko, co superadmin
			    wpisał, a jeszcze nie zapisał. Sprawdzenie drugiego segmentu przed
			    zapisem to normalny ruch, a kosztowało utratę całego formularza.
			    UWAGA: przy `forceMount` Radix ustawia `present=true`, więc NIE
			    dokłada już `hidden` — chowanie nieaktywnej zakładki należy do nas.
			    Robi to atrybut `hidden` na wewnętrznym `div`, a nie klasa Tailwinda,
			    żeby test w jsdom (gdzie Tailwind nie działa) widział to samo co
			    przeglądarka. */}
			{SEGMENTY.map((s) => (
				<TabsContent key={s.value} value={s.value} className="pt-4" forceMount>
					<div hidden={s.value !== aktywny}>
						{stan && (
							<ZrodloSegmentForm
								source={zrodla.get(s.value) as CennikZrodlo}
								wartosci={stan[s.value]}
								onChange={(w) => setStan((poprz) => (poprz ? { ...poprz, [s.value]: w } : poprz))}
							/>
						)}
					</div>
				</TabsContent>
			))}

			{/* JEDEN przycisk na cały dialog — zapisuje każdą zmienioną zakładkę.
			    Osobne przyciski per zakładka sprawiały, że po skonfigurowaniu AW i AF
			    zapisywał się tylko segment, na którym akurat stałeś. */}
			<div className="flex items-center justify-end gap-3 pt-2 max-md:flex-wrap">
				{zmienione.length > 0 && (
					<span className="text-[11px] text-muted-foreground">
						Niezapisane zmiany: {zmienione.map((s) => s.label).join(", ")}
					</span>
				)}
				<Button
					type="button"
					className="max-md:h-11 max-md:w-full"
					onClick={() => zapis.mutate()}
					disabled={zapis.isPending || zmienione.length === 0}
				>
					{zapis.isPending ? "Zapisywanie…" : "Zapisz"}
				</Button>
			</div>
		</Tabs>
	);
}

/** Kontrolowany dialog edycji źródeł cennika hurtowni. Współdzielony przez
 * komórkę „Źródła" oraz panel przeglądu — jedno miejsce definicji dialogu.
 * Body renderowane tylko gdy otwarty (leniwe pobranie źródeł). */
function ZrodlaEditDialog({
	contractorId,
	nazwa,
	defaultSegment,
	open,
	onOpenChange,
}: {
	contractorId: number;
	nazwa?: string;
	defaultSegment?: SegmentValue;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}) {
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-lg">
				<DialogHeader>
					<DialogTitle>Źródła cennika — {nazwa || `#${contractorId}`}</DialogTitle>
				</DialogHeader>
				<DialogBody>
					{open && <ZrodlaDialogBody contractorId={contractorId} defaultSegment={defaultSegment} />}
				</DialogBody>
			</DialogContent>
		</Dialog>
	);
}

/** Komórka „Źródła cennika". Super_admin: przycisk otwierający dialog konfiguracji.
 * Pozostali: read-only status bez danych dostępowych. */
function ZrodlaCell({ row }: { row: ContractorRow }) {
	const { hasRole } = useAuth();
	const isSuperAdmin = hasRole("super_admin");
	const [open, setOpen] = useState(false);

	if (!isSuperAdmin) {
		return <Badge className="text-muted-foreground opacity-70 uppercase text-[11px]">Ukryte</Badge>;
	}

	return (
		<>
			<Button
				type="button"
				variant="outline"
				size="sm"
				className="gap-1.5 max-md:h-9"
				onClick={() => setOpen(true)}
			>
				<Server className="size-3.5" />
				Źródła
			</Button>
			<ZrodlaEditDialog
				contractorId={row.id}
				nazwa={row.nazwa}
				open={open}
				onOpenChange={setOpen}
			/>
		</>
	);
}

// Kolumny szablonu Excel — DOKŁADNA kolejność (musi zgadzać się z parserem backendu).
const ZRODLA_TEMPLATE_HEADER = [
	"id",
	"nazwa",
	"ftpad",
	"ftpus",
	"ftppw",
	"ftpws",
	"idfirm",
	"ftpad_franczyza",
	"ftpus_franczyza",
	"ftppw_franczyza",
	"ftpws_franczyza",
] as const;

const ZRODLA_TEMPLATE_EXAMPLE: (string | number)[] = [
	66223,
	"HURTAP S.A.",
	"le.hurtap.com.pl",
	"887679",
	"xxdfe",
	"/oferta_kat/",
	2,
	"NULL",
	"NULL",
	"NULL",
	"NULL",
];

/** Generuje i pobiera szablon .xlsx z wierszem nagłówka + przykładowym wierszem. */
async function downloadZrodlaTemplate(): Promise<void> {
	const ExcelJS = await loadExcelJs();
	const workbook = new ExcelJS.Workbook();
	const ws = workbook.addWorksheet("Zrodla");
	ws.addRow([...ZRODLA_TEMPLATE_HEADER]);
	ws.addRow(ZRODLA_TEMPLATE_EXAMPLE);
	ws.getRow(1).font = { bold: true };

	const buffer = await workbook.xlsx.writeBuffer();
	const blob = new Blob([buffer], {
		type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	});
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = "szablon-zrodla-cennika.xlsx";
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	URL.revokeObjectURL(url);
}

/** Kontrolki importu źródeł cennika z pliku Excel (super_admin): pobranie szablonu
 * oraz upload .xlsx. Po udanym imporcie: toast + dialog z pełnym raportem. */
function ImportZrodlaControls() {
	const queryClient = useQueryClient();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [report, setReport] = useState<ImportZrodlaReport | null>(null);

	const importMutation = useMutation({
		mutationFn: (file: File) => contractsApi.importSourcesXlsx(file, true),
		onSuccess: (res) => {
			queryClient.invalidateQueries({ queryKey: ["contractors", "list-full"] });
			queryClient.invalidateQueries({ queryKey: ["contractors", "zrodla", "all"] });
			toast.success(
				`Import zakończony: utworzono ${res.utworzone}, zaktualizowano ${res.zaktualizowane}.`,
			);
			setReport(res);
		},
		onError: (err: Error) => {
			toast.error(err.message);
		},
	});

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		// Reset od razu, aby ponowny wybór tego samego pliku znów wyzwolił onChange.
		e.target.value = "";
		if (file) importMutation.mutate(file);
	};

	return (
		<>
			<Button
				type="button"
				variant="outline"
				size="sm"
				className="gap-1.5 max-md:h-9"
				onClick={() => {
					downloadZrodlaTemplate().catch((err: unknown) => {
						toast.error(
							`Nie udało się wygenerować szablonu: ${
								err instanceof Error ? err.message : String(err)
							}`,
						);
					});
				}}
			>
				<DownloadCloud className="size-4" />
				Pobierz szablon
			</Button>

			<Button
				type="button"
				variant="outline"
				size="sm"
				className="gap-1.5 max-md:h-9"
				disabled={importMutation.isPending}
				onClick={() => fileInputRef.current?.click()}
			>
				<Upload className="size-4" />
				{importMutation.isPending ? "Importowanie…" : "Importuj z Excela"}
			</Button>
			<input
				ref={fileInputRef}
				type="file"
				accept=".xlsx"
				className="hidden"
				onChange={handleFileChange}
			/>

			<Dialog open={report !== null} onOpenChange={(open) => !open && setReport(null)}>
				<DialogContent className="max-w-lg">
					<DialogHeader>
						<DialogTitle>Raport importu źródeł cennika</DialogTitle>
					</DialogHeader>
					<DialogBody>
						{report && (
							<div className="flex flex-col gap-4 text-sm">
								<div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
									<span className="text-muted-foreground">Utworzone</span>
									<span className="font-semibold text-right">{report.utworzone}</span>
									<span className="text-muted-foreground">Zaktualizowane</span>
									<span className="font-semibold text-right">{report.zaktualizowane}</span>
									<span className="text-muted-foreground">Pominięte (istniejące)</span>
									<span className="font-semibold text-right">{report.pominieteIstniejace}</span>
									<span className="text-muted-foreground">Segmentów</span>
									<span className="font-semibold text-right">{report.segmentow}</span>
								</div>

								{report.pominieteWiersze.length > 0 && (
									<div className="flex flex-col gap-1.5">
										<span className="font-medium text-status-warning-text">
											Pominięte wiersze ({report.pominieteWiersze.length})
										</span>
										<div className="max-h-40 overflow-auto rounded border border-border bg-muted/40 p-2 text-xs">
											{report.pominieteWiersze.map((w) => (
												<div key={w.row} className="flex gap-2">
													<span className="font-mono text-muted-foreground">w. {w.row}</span>
													<span>{w.powod}</span>
												</div>
											))}
										</div>
									</div>
								)}

								{report.nieznaneId.length > 0 && (
									<div className="flex flex-col gap-1.5">
										<span className="font-medium text-status-warning-text">
											Nieznane ID ({report.nieznaneId.length}) — zaimportowane, do weryfikacji
										</span>
										<div className="flex flex-wrap gap-1.5">
											{report.nieznaneId.map((id) => (
												<Badge key={id} className="font-mono text-[11px]">
													{id}
												</Badge>
											))}
										</div>
									</div>
								)}
							</div>
						)}
					</DialogBody>
				</DialogContent>
			</Dialog>
		</>
	);
}

// ── Przegląd wszystkich skonfigurowanych źródeł cennika (super_admin) ────────

/** Krótki format daty (pl-PL) — puste pole zwraca "—". */
function formatShortDate(iso?: string): string {
	if (!iso) return "—";
	const d = new Date(iso);
	return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("pl-PL");
}

/** Znacznik obecności (✓/—) w komórce tabeli. */
function CheckMark({ on }: { on: boolean }) {
	return on ? (
		<span className="text-status-success-text font-semibold">✓</span>
	) : (
		<span className="text-muted-foreground">—</span>
	);
}

/** Panel przeglądu (tylko super_admin): kompaktowa tabela WSZYSTKICH
 * skonfigurowanych źródeł cennika (baza/franczyza per hurtownia). Odświeżany
 * przez invalidację ["contractors","zrodla","all"] po zapisie/imporcie. */
function ZrodlaOverviewPanel() {
	const { data, isLoading } = useQuery({
		queryKey: ["contractors", "zrodla", "all"],
		queryFn: contractsApi.getAllContractorSources,
	});

	const rows: CennikZrodloOverviewRow[] = data ?? [];

	// Domyślnie zwinięte — panel nie spycha tabeli kontrahentów.
	const [expanded, setExpanded] = useState(false);
	// Aktualnie edytowana hurtownia (klik w wiersz przeglądu). null = zamknięte.
	const [editing, setEditing] = useState<{
		idfdost: number;
		nazwa?: string;
		segment: SegmentValue;
	} | null>(null);

	return (
		<Collapsible open={expanded} onOpenChange={setExpanded} asChild>
			<Card className="py-4">
				<CardHeader className="px-4 [.border-b]:pb-0">
					<button
						type="button"
						onClick={() => setExpanded((v) => !v)}
						aria-expanded={expanded}
						className="flex w-full items-center gap-2 text-left"
					>
						{expanded ? (
							<ChevronDown className="size-4 text-muted-foreground" />
						) : (
							<ChevronRight className="size-4 text-muted-foreground" />
						)}
						<CardTitle className="text-sm">Skonfigurowane źródła cennika ({rows.length})</CardTitle>
					</button>
				</CardHeader>
				<CollapsibleContent>
					<CardContent className="px-4">
						{isLoading ? (
							<p className="text-sm text-muted-foreground">Wczytywanie…</p>
						) : rows.length === 0 ? (
							<p className="text-sm text-muted-foreground">
								Brak skonfigurowanych źródeł — użyj importu lub przycisku Źródła przy hurtowni.
							</p>
						) : (
							<div className="overflow-auto rounded-md border border-border">
								<table className="w-full text-sm border-collapse">
									<thead>
										<tr className="border-b border-border bg-muted/40 text-left text-xs text-muted-foreground">
											<th className="px-3 py-2 font-medium">Hurtownia</th>
											<th className="px-3 py-2 font-medium">Segment</th>
											<th className="px-3 py-2 font-medium">Typ</th>
											<th className="px-3 py-2 font-medium">Host</th>
											<th className="px-3 py-2 font-medium">Katalog</th>
											<th className="px-3 py-2 font-medium">Login</th>
											<th className="px-3 py-2 font-medium text-center">Hasło</th>
											<th className="px-3 py-2 font-medium text-center">FTPS</th>
											<th className="px-3 py-2 font-medium text-center">Aktywne</th>
											<th className="px-3 py-2 font-medium">Zaktualizowano</th>
										</tr>
									</thead>
									<tbody>
										{rows.map((r) => {
											const openEditor = () =>
												setEditing({
													idfdost: r.idfdost,
													nazwa: r.nazwa,
													segment: r.segment,
												});
											return (
												// biome-ignore lint/a11y/useSemanticElements: klikalny wiersz tabeli nie moze byc <button>
												<tr
													key={`${r.idfdost}-${r.segment}`}
													role="button"
													tabIndex={0}
													onClick={openEditor}
													onKeyDown={(e) => {
														if (e.key === "Enter" || e.key === " ") {
															e.preventDefault();
															openEditor();
														}
													}}
													className="cursor-pointer border-b border-border last:border-0 hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none"
												>
													<td className="px-3 py-2 font-medium">{r.nazwa || `#${r.idfdost}`}</td>
													<td className="px-3 py-2">
														<Badge className="uppercase text-[11px]">
															{r.segment === "baza" ? "AW" : "AF"}
														</Badge>
													</td>
													<td className="px-3 py-2 font-mono text-xs">{r.typ}</td>
													<td className="px-3 py-2 font-mono text-xs text-muted-foreground">
														{r.host || "—"}
													</td>
													<td className="px-3 py-2 font-mono text-xs text-muted-foreground">
														{r.katalog || "—"}
													</td>
													<td className="px-3 py-2 font-mono text-xs text-muted-foreground">
														{r.login || "—"}
													</td>
													<td className="px-3 py-2 text-center">
														<CheckMark on={r.hasloUstawione} />
													</td>
													<td className="px-3 py-2 text-center">
														<CheckMark on={r.uzyjFtps} />
													</td>
													<td className="px-3 py-2 text-center">
														<Badge
															className={
																r.aktywne
																	? "text-status-success-text uppercase text-[11px]"
																	: "text-muted-foreground opacity-70 uppercase text-[11px]"
															}
														>
															<CheckMark on={r.aktywne} />
														</Badge>
													</td>
													<td className="px-3 py-2">
														<div className="flex flex-col leading-tight">
															<span className="text-xs">{formatShortDate(r.updatedAt)}</span>
															{r.updatedBy && (
																<span className="text-[11px] text-muted-foreground">
																	{r.updatedBy}
																</span>
															)}
														</div>
													</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
						)}
					</CardContent>
				</CollapsibleContent>
				<ZrodlaEditDialog
					contractorId={editing?.idfdost ?? 0}
					nazwa={editing?.nazwa}
					defaultSegment={editing?.segment}
					open={editing !== null}
					onOpenChange={(open) => {
						if (!open) setEditing(null);
					}}
				/>
			</Card>
		</Collapsible>
	);
}

export const Route = createFileRoute("/_auth/kartoteka/kontrahenci")({
	beforeLoad: async ({ context, location }) => {
		await ensureElementVisible(
			context.queryClient,
			"sidebar.kartoteka.kontrahenci",
			location.pathname,
		);
	},
	component: ContractorsListPage,
});

const columns: AdminTableColumn<ContractorRow>[] = [
	{
		key: "id",
		header: "ID",
		width: "80px",
		sortable: true,
		// Telefon: identyfikator jako podtytul, tytulem wiersza jest nazwa.
		mobileRole: "subtitle",
		render: (row) => (
			<div className="flex items-center gap-1.5 text-muted-foreground font-mono text-xs">
				<Hash className="size-3 opacity-50" />
				{row.id}
			</div>
		),
	},
	{
		key: "nazwa",
		header: "Nazwa Kontrahenta",
		sortable: true,
		mobileRole: "title",
		render: (row) => <span className="font-semibold text-sm">{row.nazwa || "—"}</span>,
	},
	{
		key: "exclusion",
		header: "Wykluczony z zakupów",
		sortable: true,
		mobileRole: "detail",
		mobileLabel: "Wykluczony",
		render: (row) => {
			const isExcluded =
				row.connectedFirm === true &&
				(row.includetoneworder === false || (row.linkCount ?? 0) === 0);
			if (isExcluded) {
				return <Badge className="bg-red-50 text-red-700 border-red-200 uppercase">TAK</Badge>;
			}
			return <Badge className="text-muted-foreground opacity-50 uppercase">NIE</Badge>;
		},
	},
	{
		key: "linkCount",
		header: "Powiązane apteki",
		sortable: true,
		numeric: true,
		mobileLabel: "Apteki",
		render: (row) => <div className="text-sm font-medium">{row.linkCount ?? 0}</div>,
	},
	{
		key: "formulaWariant",
		header: "Formuła ceny (RDA)",
		sortable: true,
		mobileRole: "detail",
		render: (row) => <FormulaCell row={row} />,
	},
	{
		key: "zrodla",
		header: "Źródła cennika",
		// Telefon: przycisk „Źródła” (dialog konfiguracji) w stopce szczegolow wiersza.
		mobileRole: "actions",
		render: (row) => <ZrodlaCell row={row} />,
	},
];

function ContractorsListPage() {
	const { hasRole } = useAuth();
	const isSuperAdmin = hasRole("super_admin");
	const isBelowMd = useIsBelowMd();
	const [searchQuery, setSearchQuery] = useState("");
	const [filter, setFilter] = useState<"all" | "excluded">("all");
	const [sort, setSort] = useState<SortState | null>({ column: "nazwa", dir: "asc" });
	const debouncedQuery = useDebounce(searchQuery, 300);

	const { data: contractors = [] } = useQuery({
		queryKey: ["contractors", "list-full"],
		queryFn: contractsApi.getContractorsList,
	});

	const processedData = [...contractors]
		.filter((c) => {
			const matchesSearch =
				!debouncedQuery || c.nazwa?.toLowerCase().includes(debouncedQuery.toLowerCase());
			// Definicja wykluczenia: powiazany z centrala I (zablokowane zamowienia LUB brak powiazanych aptek)
			const isExcluded =
				c.connectedFirm === true && (c.includetoneworder === false || (c.linkCount ?? 0) === 0);
			const matchesFilter = filter === "all" || (filter === "excluded" && isExcluded);
			return matchesSearch && matchesFilter;
		})
		.sort((a, b) => {
			if (!sort) return 0;
			const { column, dir } = sort;

			let valA: string | number | boolean = "";
			let valB: string | number | boolean = "";

			if (column === "exclusion") {
				valA =
					a.connectedFirm === true && (a.includetoneworder === false || (a.linkCount ?? 0) === 0);
				valB =
					b.connectedFirm === true && (b.includetoneworder === false || (b.linkCount ?? 0) === 0);
			} else {
				const key = column as keyof ContractorRow;
				const rawA = a[key];
				const rawB = b[key];
				valA =
					typeof rawA === "string" || typeof rawA === "number" || typeof rawA === "boolean"
						? rawA
						: "";
				valB =
					typeof rawB === "string" || typeof rawB === "number" || typeof rawB === "boolean"
						? rawB
						: "";
			}

			if (valA < valB) return dir === "asc" ? -1 : 1;
			if (valA > valB) return dir === "asc" ? 1 : -1;
			return 0;
		});

	const filterTabs = (
		<Tabs
			value={filter}
			onValueChange={(v) => setFilter(v as "all" | "excluded")}
			className="w-auto"
		>
			<TabsList>
				<TabsTrigger value="all">Wszyscy</TabsTrigger>
				<TabsTrigger value="excluded">Wykluczeni</TabsTrigger>
			</TabsList>
		</Tabs>
	);

	return (
		<div className="flex flex-col h-full overflow-hidden">
			<div className="p-6 pb-2 max-md:px-3 max-md:pt-3 max-md:pb-1 max-md:empty:hidden">
				<PageHeader
					title="Kontrahenci centrali"
					description="Lista kontrahentów zsynchronizowana z tabelą dostawców (fdost)"
					// Telefon: import zrodel (super_admin) ląduje pod paskiem gornym,
					// bo rzad filtrow zwija sie do chipow. Desktop bez zmian.
					actions={isBelowMd && isSuperAdmin ? <ImportZrodlaControls /> : undefined}
				/>
			</div>

			{isBelowMd && (
				<div className="px-3 pt-2 pb-2 border-t bg-card/50">
					<ProductSearchBar
						value={searchQuery}
						onChange={setSearchQuery}
						placeholder="Szukaj po nazwie kontrahenta..."
					/>
				</div>
			)}
			<MobileFilters
				chips={[
					{
						id: "filter",
						label: "Kontrahenci",
						value: filter === "excluded" ? "Wykluczeni" : "Wszyscy",
						active: filter !== "all",
						picker: filterTabs,
					},
				]}
				sheetContent={<MobileFilterField label="Kontrahenci">{filterTabs}</MobileFilterField>}
				onClear={() => setFilter("all")}
				resultLabel={`Pokaż ${processedData.length} kontrahentów`}
			>
				<div className="px-6 py-3 border-y bg-card/50 flex flex-wrap gap-4 justify-between items-center">
					<div className="flex items-center gap-4 flex-1 min-w-[300px]">
						<div className="w-full max-w-md">
							<ProductSearchBar
								value={searchQuery}
								onChange={setSearchQuery}
								placeholder="Szukaj po nazwie kontrahenta..."
							/>
						</div>
						{filterTabs}
					</div>
					<div className="flex items-center gap-4">
						{isSuperAdmin && <ImportZrodlaControls />}
						<div className="text-xs text-muted-foreground hidden sm:block">
							Liczba kontrahentów: <span className="font-bold">{processedData.length}</span>
						</div>
					</div>
				</div>
			</MobileFilters>

			{isSuperAdmin && (
				<div className="px-6 pt-4 max-md:px-3 max-md:pt-2">
					<ZrodlaOverviewPanel />
				</div>
			)}

			<div className="flex-1 min-h-0 p-6 pt-3 max-md:p-0 max-md:pt-2">
				<AdminTable
					tableId="kartoteka.kontrahenci"
					columns={columns}
					data={processedData}
					keyField="id"
					sort={sort}
					onSortChange={setSort}
					emptyMessage="Nie znaleziono kontrahentów spełniających kryteria."
					className="h-full"
				/>
			</div>
		</div>
	);
}
